import { policyResultKeys } from './policy-order-api'
import { decodePolicyMutation, decodePolicyRefreshStatus, type PolicyRefreshStatus } from '../lib/policy-authoring'
import type { components } from '@/lib/openapi.generated'
import {
  isRecord,
  json,
  nullableNumber,
  nullableString,
  request,
  requireBoolean,
  requireNumber,
  requireString,
  requireTimestamp,
  withQuery,
} from '@/lib/api'

type Schemas = components['schemas']
export const contentUploadPolicyKeys = {
  result: (entityId: string) => policyResultKeys.type(entityId, CONTENT_UPLOAD_POLICY_TYPE),
  own: (entityId: string) => [...policyResultKeys.type(entityId, CONTENT_UPLOAD_POLICY_TYPE), 'own'] as const,
}
export const CONTENT_UPLOAD_POLICY_TYPE = 'entity:content.media.max-bytes'
export type UploadLimitDefinition = Pick<Schemas['ContentUploadPolicyResponse'], 'id' | 'revision' | 'value'>
export type DefineUploadLimit = Omit<Schemas['SetContentUploadLimitRequest'], 'mode' | 'value' | 'operationId'> & {
  mode: 'Define'
  value: number
  operationId: string
}
export type UploadLimitWrite = DefineUploadLimit
  | (Pick<Schemas['SetContentUploadLimitRequest'], 'expectedDefinition'> & { mode: 'Inherit' })
export type { PolicyRefreshStatus }
export type ContentUploadPolicyResult = Omit<
  Schemas['ContentUploadPolicyResultResponse'],
  'selectionStatus' | 'refreshStatus'
> & {
  selectionStatus: 'NeverEvaluated' | 'Evaluated'
  refreshStatus: PolicyRefreshStatus
}

function decodeDefinition(raw: unknown): UploadLimitDefinition {
  if (!isRecord(raw)) throw new Error('Malformed upload policy')
  return {
    id: requireString(raw.id, 'id'),
    revision: requireNumber(raw.revision, 'revision'),
    value: requireNumber(raw.value, 'value'),
  } satisfies UploadLimitDefinition
}

function decodeOwnDefinition(raw: unknown): UploadLimitDefinition | null {
  if (!isRecord(raw) || !Array.isArray(raw.items)) throw new Error('Malformed upload policy list')
  if (raw.items.length > 1) throw new Error('Multiple upload limit definitions need an administrator to finish the upgrade.')
  return raw.items.length === 0 ? null : decodeDefinition(raw.items[0])
}

function decodeSetResult(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Malformed upload limit result')
  return {
    mode: requireString(raw.mode, 'mode'),
    mutation: raw.mutation == null ? null : decodePolicyMutation(raw.mutation),
  }
}

function decodeResult(raw: unknown): ContentUploadPolicyResult {
  if (!isRecord(raw)) throw new Error('Malformed upload policy result')
  if (raw.selectionStatus !== 'NeverEvaluated' && raw.selectionStatus !== 'Evaluated')
    throw new Error('Unexpected policy selection status')
  return {
    selectionStatus: raw.selectionStatus,
    selectionRevision: nullableNumber(raw.selectionRevision, 'selectionRevision'),
    evaluatedAt: raw.evaluatedAt == null ? null : requireTimestamp(raw.evaluatedAt, 'evaluatedAt'),
    refreshStatus: decodePolicyRefreshStatus(raw.refreshStatus),
    failureCode: nullableString(raw.failureCode, 'failureCode'),
    value: nullableNumber(raw.value, 'value'),
    isDefault: requireBoolean(raw.isDefault, 'isDefault'),
  } satisfies ContentUploadPolicyResult
}

export const contentUploadPolicyApi = {
  result: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/content/upload-policy-result`, decodeResult),
  own: (entityId: string) =>
    request(withQuery(`/api/v1/entities/${entityId}/content/upload-policies`, { limit: '2' }), decodeOwnDefinition),
  set: (entityId: string, write: UploadLimitWrite) =>
    request(
      `/api/v1/entities/${entityId}/content/upload-limit`,
      decodeSetResult,
      json('PUT', write satisfies Schemas['SetContentUploadLimitRequest']),
    ),
}
